import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Icon from '@mui/material/Icon';
import CircularProgress from '@mui/material/CircularProgress';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GrowIcon from '@mui/icons-material/ZoomOutMap';
import ShrinkIcon from '@mui/icons-material/ZoomInMap';

import noSensors from '../../../images/no-sensors.png'

import SensorCard from '../../components/sensors/sensorcard';
import PolicySidebar from '../../components/sensors/policysidebar';

import SensorsService from '../../data/sensors-service';
import PolicyService from '../../data/policy-service';
import Auth, { ProfileContext } from '../../data/auth-service';
import DataContext from '../../data/data-context';

import useInterval from '../../libraries/useinterval';
import BacktestingService from '../../data/backtesting-service';

const useStyles = makeStyles(theme => ({
    content: {
        marginTop: '-40px',
        flexGrow: 1,
        // padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: 0,
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: '310px',
    },
    contentShiftSmall: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: '60px',
    },
    fullWidth: {
        width: '100%',
    },
    paperBackground: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    allSensorBackground: {
        backgroundColor: theme.palette.background.paper
    },
    policyBar: {
        width: '60px',
        background: theme.palette.common.black,
        marginTop: '80px',
        marginLeft: '60px',
    },
    loading: {
        marginTop: '250px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
    },
    emptyState: {
        marginTop: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    image: {
        objectFit: 'contain', // Maintain aspect ratio and fit inside the box
        maxWidth: '300px',
        minWidth: '100px',
    },
    closedAccordion: {
        display: 'none',
        height: '0px',
        minHeight: '0px',
    },
    menuItem: {
        minWidth: '150px',
    },
    normalOutlined: {
        borderColor: theme.palette.success.main,
        color: theme.palette.success.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.success.dark,
            color: theme.palette.success.dark,
        }
    },
    normalContained: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.success.dark,
        }
    },
    warningOutlined: {
        borderColor: theme.palette.warning.main,
        color: theme.palette.warning.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.warning.dark,
            color: theme.palette.warning.dark,
        }
    },
    warningContained: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.warning.dark,
        }
    },
    criticalOutlined: {
        borderColor: theme.palette.error.main,
        color: theme.palette.error.main,
        '&:hover, &:focus, &:active': {
            borderColor: theme.palette.error.dark,
            color: theme.palette.error.dark,
        }
    },
    criticalContained: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.error.dark,
        }
    },
    policyFilterListItem: {
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '12px',
        color: theme.palette.primary.main,
        height: '32px',
        padding: '5px',
        cursor: 'pointer',
        marginRight: '10px',
        marginBottom: '8px',
    },
    policyFilterCritical: {
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
    },
    policyFilterWarning: {
        border: `1px solid ${theme.palette.warning.main}`,
        color: theme.palette.warning.main,
        background: theme.palette.mode === 'light' ? '#000' : 'inherit',
    },
    policyFilterNormal: {
        border: `1px solid ${theme.palette.success.main}`,
        color: theme.palette.success.main,
    },
    policyFilterCloseIcon: {
        width: '18px',
        marginTop: '-1px',
        marginLeft: '2px',
    },
}));

const SORT_TYPES = [
    { value: 'status', name: 'Status' },
    { value: 'name', name: 'Name' },
    { value: 'host', name: 'Host' },
    // {value: 'delivery', name: 'Last Delivery'},
    // {value: 'alert', name: 'Last Alert'},
];

const Sensors = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const { setSidenav } = useContext(DataContext);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [smallCards, setSmallCards] = useState(false);
    const [filter, setFilter] = useState({
        q: '',
        limit: 1000,
        name: '',
        host: '',
        model: '',
        status: {
            critical: true,
            warning: true,
            normal: true,
        },
        policies: [],
        sort: 'status+desc',
    });
    const [filterType, setFilterType] = useState('search');
    const [sensors, setSensors] = useState([]);
    const [isLoading, setIsLoading] = useState(true)
    const [filteredSensors, setFilteredSensors] = useState([]);
    const [, setInit] = useState(true);
    const [sortAnchor, setSortAnchor] = useState(null);
    const { updateProfile, getProfile, perm } = useContext(ProfileContext);
    const [policies, setPolicies] = useState([]);
    const [applianceFilter, setApplianceFilter] = useState({
        sensor: true,
        btsensor: true,
    });
    const sensorPerms = Auth.permissions.get('Sensors');
    const policyRead = perm({
        GeoIPPolicy: ['read'],
        ExportsPolicy: ['read'],
        PcapPolicy: ['read'],
        DataTuningPolicy: ['read'],
        FileHashPolicy: ['read'],
        ConfigPolicy: ['read'],
        UpgradePolicy: ['read'],
        SuricataRulesPolicy: ['read'],
        SuricataVarsPolicy: ['read'],
        SuricataRules: ['read'],
        ZeekScriptsPolicy: ['read'],
        ThreatIntelPolicy: ['read'],
    });

    const getPolicies = () => {
        if (policyRead) {
            PolicyService.getPoliciesWithTypes().then(res => {
                setPolicies(res);
            }, err => {
                console.error('Unable to get policies and types ::: ', err);
            });
        }
    };

    const getSensors = (search, applianceFilter) => {
        SensorsService.getData(search).then(res => {
            if (!res) res = [];
            setFilteredSensors(res);
            setSensors(applyApplianceFilter(res, applianceFilter));
            setInit(false);
            setTimeout(() => {
                setIsLoading(false)
            }, 1000)
        });
    };

    const applyApplianceFilter = (res, appFil) => {
        const s = res.filter(v => {
            const bt = _get(v, 'data.backtesting', '');
            if (appFil.sensor && !bt) {
                return v;
            } else if (appFil.btsensor && bt) {
                return v;
            } else if (!appFil.sensor && !appFil.btsensor) {
                return v;
            }
            return null;
        });

        return s;
    };

    const buildFilter = useCallback(() => {
        const f = Object.keys(filter).reduce((a, c) => {
            switch (c) {
                case 'status':
                    a = Array.prototype.concat(a, Object.keys(filter[c]).map((v) => {
                        if (filter[c][v]) return 'status=' + encodeURIComponent(v);
                        return null;
                    }));
                    break;
                case 'sort':
                    a.push('sort=' + filter[c]);
                    break;
                case 'policies':
                    a = Array.prototype.concat(a, filter[c].map((v) => {
                        return 'filter=' + encodeURIComponent(`${v.type}=${v.name}`);
                    }));
                    break;
                case 'q':
                    if (filter[c]) a.push('q=' + encodeURIComponent(filter[c].toLowerCase()));
                    break;
                case 'limit':
                    if (filter[c]) a.push('limit=' + filter[c]);
                    break;
                default:
                    if (filter[c]) a.push('filter=' + encodeURIComponent(`${c}=${filter[c]}`));
            }

            return a;
        }, []);

        return f.join('&');
    }, [filter]);

    useEffect(() => {
        // Thanks MUIv5 for this...
        setTimeout(() => {
            setSidenav({ sidebar: policyRead ? 'collapsed' : null });
        });

        getSensors(buildFilter(), applianceFilter);
        setSmallCards(getProfile('sensor').smallCards || false);
        getPolicies();

        const policyInterval = setInterval(() => {
            getPolicies();
        }, 15000);

        return () => {
            clearInterval(policyInterval);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getSensors(buildFilter(), applianceFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applianceFilter]);

    useInterval(() => {
        getSensors(buildFilter(), applianceFilter);
    }, 15000);

    const handleSelect = (uuid) => {
        navigate(`/sensors/health/${uuid}`);
    };

    const handleUpdate = (uuid) => {
        navigate(`/sensors/edit/${uuid}`);
    };

    // const handleClosePolicy = () => {
    //     setFilter({
    //         ...filter,
    //         policies: [],
    //     });
    // };

    const handlePolicySelect = (type, name) => {
        const f = { ...filter };
        const typeAndPolicyExists = f.policies.findIndex(v => (v.type === type && v.name === name));
        const typeExists = f.policies.findIndex(v => (v.type === type));

        if (typeAndPolicyExists !== -1) {
            f.policies.splice(typeAndPolicyExists, 1);
        } else if (typeAndPolicyExists === -1 && typeExists !== -1) {
            f.policies.splice(typeExists, 1);
            f.policies.push({
                type: type,
                name: name,
            });
        } else {
            f.policies.push({
                type: type,
                name: name,
            });
        }

        setFilter(f);
    };

    const handleToggleFilters = () => {
        setFiltersOpen(!filtersOpen);
    };

    const handleAddSensor = () => {
        navigate('/sensors/add');
    };

    const handleChangeFilterType = () => {
        if (filterType === 'search') {
            setFilterType('fields');
            setFilter({
                ...filter,
                name: '',
                host: '',
                model: '',
                q: '',
                limit: 1000,
            });
        } else {
            setFilterType('search');
            setFilter({
                ...filter,
                name: '',
                host: '',
                model: '',
                q: '',
                limit: 1000,
            });
        }
    };

    const handleUpdateFilter = (e) => {
        setFilter({
            ...filter,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdateStatus = (e) => {
        const f = { ...filter };
        f.status[e.currentTarget.name] = !f.status[e.currentTarget.name];
        setFilter(f);
    };

    const handleUpdateSort = (s) => {
        let d = 'desc';

        if (filter.sort.split('+')[0] === s) {
            d = (filter.sort.split('+')[1] === 'desc' ? 'asc' : 'desc');
        }

        setFilter({
            ...filter,
            sort: `${s}+${d}`,
        });
    };

    const handleResetFilters = () => {
        setFilter({
            q: '',
            name: '',
            host: '',
            model: '',
            status: { critical: true, warning: true, normal: true },
            policies: [],
            sort: 'status+desc',
            limit: 1000,
        });
    };

    const handleAddPolicyAll = (policy) => {
        PolicyService.assignAllPolicy(policy.type, policy.name).then((res) => {
            toast.success('Successfully pushed policy to all sensors. It may take some time for the policy to be delivered to the sensors.');
            setFilter({ ...filter });
        });
    };

    const handleAddPolicyFiltered = (policy) => {
        console.log('handleAddPolicyFiltered', policy);
        if (filteredSensors.length) {
            PolicyService.assignPolicy(
                policy.type,
                policy.name,
                filteredSensors.filter(v => {
                    const bt = _get(v, 'data.backtesting', '');
                    return !bt || !BacktestingService.policies[policy.type];
                }).map(v => (v.uuid)),
            ).then(res => {
                toast.success('Successfully pushed policy to sensors in filter');
                setFilter(filter);
            });
        } else if (sensors.length) {
            PolicyService.assignPolicy(
                policy.type,
                policy.name,
                sensors.filter(v => {
                    const bt = _get(v, 'data.backtesting', '');
                    return !bt || !BacktestingService.policies[policy.type];
                }).map(v => v.uuid),
            ).then(res => {
                toast.success('Successfully pushed policy to sensors in filter');
                setFilter(filter);
            });
        } else {
            toast.warn('Policy not pushed. No sensors in filter.');
        }
    };

    const handleOpenSort = (e) => {
        setSortAnchor(e.currentTarget);
    };

    const handleCloseSort = () => {
        setSortAnchor(null);
    };

    const handleResizeCard = () => {
        setSmallCards(!smallCards);
        updateProfile('sensor', {
            ...getProfile('sensor'),
            smallCards: !smallCards,
        });
    };

    const handleClearFilter = (field) => {
        const f = { ...filter };
        f[field] = '';
        setFilter(f);
    };

    const handleClearStatus = (status) => {
        const f = { ...filter };
        f.status[status] = false;
        setFilter(f);
    };

    const handleUpdateApplianceFilter = (e) => {
        const a = { ...applianceFilter };
        a[e.currentTarget.name] = !a[e.currentTarget.name];
        setApplianceFilter(a);
    };

    return (
        <React.Fragment>
            {policyRead && <PolicySidebar policies={policies} onSelectPolicy={handlePolicySelect} onAddToAll={handleAddPolicyAll} onAddToFiltered={handleAddPolicyFiltered}/>}
            <main className={classes.content}>
                <Grid container direction="row" justifyContent="center" alignItems="center">
                    <Grid item xs={12} container justifyContent="flex-end">
                        <IconButton
                            aria-label="expand or collapse extra sensor info on sensor cards"
                            onClick={handleResizeCard}
                            size="small">
                            {smallCards ? <GrowIcon/> : <ShrinkIcon/>}
                        </IconButton>
                        <Box ml={2}><Button
                            color="inherit"
                            variant="contained"
                            aria-label="Open sort menu"
                            onClick={handleOpenSort}
                        >Sort</Button></Box>
                        <Box ml={2}><Button
                            color="inherit"
                            variant="contained"
                            aria-label="Toggle filter menu"
                            onClick={handleToggleFilters}
                        >Filters</Button></Box>
                        {sensorPerms.write && <Box ml={2}><Button
                            color="primary"
                            variant="contained"
                            aria-label="add sensor"
                            onClick={handleAddSensor}
                        ><AddIcon/> Sensor</Button></Box>}
                        <Menu
                            anchorEl={sortAnchor}
                            open={Boolean(sortAnchor)}
                            onClose={handleCloseSort}
                        >
                            {SORT_TYPES.map((v, i) => {
                                return (
                                    <MenuItem
                                        key={'sort-menu-item-' + i}
                                        className={classes.menuItem}
                                        onClick={() => { handleUpdateSort(v.value) }}
                                    >
                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            {v.name}
                                            {filter.sort.split('+')[0] === v.value ? (filter.sort.split('+')[1] === 'desc' ? <ExpandMore/> : <ExpandLess/>) : null}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </Grid>
                    <Grid container item xs={12}>
                        <Accordion expanded={filtersOpen} className={classes.fullWidth}>
                            <AccordionSummary className={classes.closedAccordion}></AccordionSummary>
                            <AccordionDetails>
                                <Box p={2} width="100%">
                                    <Grid container spacing={2}>
                                        <Grid item lg={5} container spacing={2}>
                                            <Grid item xs={2}>
                                                <Typography variant="h6">Status</Typography>
                                            </Grid>
                                            <Grid item>
                                                <ButtonGroup>
                                                    <Button
                                                        classes={{
                                                            outlined: classes.normalOutlined,
                                                            contained: classes.normalContained,
                                                        }}
                                                        name="normal"
                                                        variant={filter.status.normal ? 'contained' : 'outlined'}
                                                        onClick={handleUpdateStatus}
                                                    >
                                                        Normal
                                                    </Button>
                                                    <Button
                                                        classes={{
                                                            outlined: classes.warningOutlined,
                                                            contained: classes.warningContained,
                                                        }}
                                                        name="warning"
                                                        variant={filter.status.warning ? 'contained' : 'outlined'}
                                                        onClick={handleUpdateStatus}
                                                    >
                                                        Warning
                                                    </Button>
                                                    <Button
                                                        classes={{
                                                            outlined: classes.criticalOutlined,
                                                            contained: classes.criticalContained,
                                                        }}
                                                        name="critical"
                                                        variant={filter.status.critical ? 'contained' : 'outlined'}
                                                        onClick={handleUpdateStatus}
                                                    >
                                                        Critical
                                                    </Button>
                                                </ButtonGroup>
                                            </Grid>
                                            <Grid item xs={12} container spacing={2}>
                                                <Grid item xs={2}>
                                                    <Typography variant="h6">Type</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <ButtonGroup>
                                                        <Button
                                                            color="primary"
                                                            name="sensor"
                                                            variant={applianceFilter.sensor ? 'contained' : 'outlined'}
                                                            onClick={handleUpdateApplianceFilter}
                                                        >
                                                            Regular
                                                        </Button>
                                                        <Button
                                                            color="primary"
                                                            name="btsensor"
                                                            variant={applianceFilter.btsensor ? 'contained' : 'outlined'}
                                                            onClick={handleUpdateApplianceFilter}
                                                        >
                                                            Backtesting
                                                        </Button>
                                                    </ButtonGroup>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container item lg={7} spacing={2}>
                                            <Grid item xs={12}>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography variant="h6">Filters</Typography>
                                                    <Box display="flex">
                                                        <Box><Button variant="contained" onClick={handleChangeFilterType}>{filterType === 'search' ? 'Fields' : 'Search'}</Button></Box>
                                                        <Box ml={2}><Button variant="contained" color="secondary" onClick={handleResetFilters}>Reset Filters</Button></Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box display={filterType === 'search' ? 'block' : 'none'} width="100%">
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <TextField fullWidth type="text" label="Search" name="q" value={filter.q} onChange={handleUpdateFilter}/>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                                <Box display={filterType === 'fields' ? 'flex' : 'none'} width="100%">
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={4}><TextField fullWidth type="text" label="Name" name="name" value={filter.name} onChange={handleUpdateFilter}/></Grid>
                                                        <Grid item xs={4}><TextField fullWidth type="text" label="Host" name="host" value={filter.host} onChange={handleUpdateFilter}/></Grid>
                                                        <Grid item xs={4}><TextField fullWidth type="text" label="Model" name="model" value={filter.model} onChange={handleUpdateFilter}/></Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
                <Box mt={2}>
                    <Grid container spacing={2} direction="row">
                        {/* <Grid container direction="row" alignContent="flex-start" spacing={2} item lg={6}>
                            <Grid item xs={12}>
                                <Paper elevation={3}>
                                    <Box display={filter.policies.length ? 'block' : 'none'} p={2} className={classes.paperBackground}>
                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            <Box display="flex" flexWrap="wrap">
                                                <Box mr={2}>
                                                    <Typography>In Filter: {filteredSensors.length}</Typography>
                                                </Box>
                                                {filter.policies.map((v, i) => {
                                                    return (
                                                        <Box
                                                            key={'filter-policy-'+i}
                                                            className={classes.policyFilterListItem}
                                                            onClick={() => {handlePolicySelect(v.type, v.name)}}
                                                        >
                                                            <Box>{(v.name === 'default' ? v.type+': ' : '')+v.name}</Box>
                                                            <Box><CloseIcon className={classes.policyFilterCloseIcon} /></Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                            <IconButton className={classes.headerButton} aria-label="close" onClick={handleClosePolicy}><CloseIcon /></IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                            {filteredSensors.map((v, i) => {
                                                return <SensorCard key={'sensor-card-'+i} sensor={v} colsize={6} small={smallCards} handlePolicySelect={handlePolicySelect} handleSelect={handleSelect} />
                                            })}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid> */}
                        {/* <Grid container direction="row" alignContent="flex-start" spacing={2} item lg={filter.policies.length ? 6 : 12}> */}
                        <Grid container direction="row" alignContent="flex-start" spacing={2} item lg={12}>
                            {/* <Box display={filter.policies.length ? 'flex' : 'none'} mt={1} p={1} className={classes.allSensorBackground} width="100%">
                                <Typography variant="h6">All Sensors: {sensors.length}</Typography>
                            </Box> */}
                            <Box
                                display={(() => {
                                    if (filter.host ||
                                        filter.model ||
                                        filter.name ||
                                        filter.q ||
                                        !((filter.status.critical && filter.status.warning && filter.status.normal) ||
                                        (!filter.status.critical && !filter.status.warning && !filter.status.normal)) ||
                                        filter.policies.length) {
                                        return 'flex';
                                    }
                                    return 'none';
                                    // return filter.policies.length ? 'flex' : 'none';
                                })()}
                                mt={1}
                                pt={1}
                                px={1}
                                className={classes.allSensorBackground}
                                width="100%">
                                <Box mr={2}><Typography variant="h6">Filters</Typography></Box>
                                {filter.policies.map((v, i) => {
                                    return (
                                        <Box
                                            key={'filter-policy-' + i}
                                            className={classes.policyFilterListItem}
                                            onClick={() => { handlePolicySelect(v.type, v.name) }}
                                            display="flex"
                                        >
                                            <Box>{(v.name === 'default' ? v.type + ': ' : '') + v.name}</Box>
                                            <Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box>
                                        </Box>
                                    );
                                })}
                                {filter.host ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('host') }}>Host: {filter.host}<Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box></Box> : null}
                                {filter.model ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('model') }}>Model: {filter.model}<Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box></Box> : null}
                                {filter.name ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('name') }}>Name: {filter.name}<Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box></Box> : null}
                                {filter.q ? <Box className={classes.policyFilterListItem} display="flex" onClick={() => { handleClearFilter('q') }}>Search: {filter.q}<Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box></Box> : null}
                                {!(filter.status.critical && filter.status.warning && filter.status.normal)
                                    ? <Box display="flex">
                                        <Box
                                            className={clsx(classes.policyFilterListItem, classes.policyFilterCritical)}
                                            display={filter.status.critical ? 'flex' : 'none'}
                                            onClick={() => { handleClearStatus('critical') }}
                                        >
                                            <Box>Status: Critical</Box>
                                            <Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box>
                                        </Box>
                                        <Box
                                            className={clsx(classes.policyFilterListItem, classes.policyFilterWarning)}
                                            display={filter.status.warning ? 'flex' : 'none'}
                                            onClick={() => { handleClearStatus('warning') }}
                                        >
                                            <Box>Status: Warning</Box>
                                            <Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box>
                                        </Box>
                                        <Box
                                            className={clsx(classes.policyFilterListItem, classes.policyFilterNormal)}
                                            display={filter.status.normal ? 'flex' : 'none'}
                                            onClick={() => { handleClearStatus('normal') }}
                                        >
                                            <Box>Status: Normal</Box>
                                            <Box><CloseIcon className={classes.policyFilterCloseIcon}/></Box>
                                        </Box>
                                    </Box>
                                    : null}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            {isLoading ? (
                                <div className={classes.loading}>
                                    <CircularProgress color="primary"/>
                                    <Typography>Trying to fetch sensors</Typography>
                                </div>
                            ) : sensors.length != 0 ? (
                                <Grid container spacing={2}>
                                    {/* {(filteredSensors.length ? filteredSensors : sensors).map((v, i) => { */}
                                    {sensors.map((v, i) => {
                                        return <SensorCard
                                            key={'sensor-card-' + i}
                                            sensor={v}
                                            policies={policies}
                                            // colsize={filter.policies.length ? 6 : 4}
                                            colsize={4}
                                            small={smallCards}
                                            handlePolicySelect={handlePolicySelect}
                                            handleSelect={() => { handleSelect(v.uuid) }}
                                            handleUpdate={() => { handleUpdate(v.uuid) }}
                                        />;
                                    })}
                                </Grid>) : (
                                <div className={classes.emptyState}>
                                    <img src={noSensors} alt="No Results" className={classes.image}/>
                                    <Typography>No sensors found</Typography>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </main>
        </React.Fragment>
    );
};

export default Sensors;
